import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { deriveInvestorStatus } from '@/api/investing/state-machine';
import { _getWizardResumeQuery } from '@/api/investing/subscription-api';
import {
  projectSubscriptionToInvestment,
  SubscriptionStatus,
  type SubscriptionStatusType,
} from '@/api/investing/subscription-projection';
import type { Investment } from '@/api/investing/types';
import { _getMyInvestmentBoard } from '@/api/investing-board/queries';
import type {
  MyInvestmentBoardColumnDto,
  MyInvestmentBoardEntryDto,
} from '@/api/investing-board/types';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { PagePaths } from '@/routes/page-paths';
import {
  EmptyState,
  getTableMinWidth,
  Spinner,
  Table,
  TableColumn,
  TextCell,
  theme,
  TIME_CELL_MIN_WIDTH,
  TimeCell,
  Typography,
} from '@/shared/design-system';
import { formatDefaultFractionTwo } from '@/utils/numbers/formatters';

import { PendingAssetActions } from './pending-asset-actions';
import { LoadingState, SectionBody, SectionHeader } from './pending-assets.styled';
import { PendingAssetRow, PendingAssetsMobile } from './pending-assets-mobile';
import { StatusBadge } from './status-badge';

const UNKNOWN_FUND_NAME = 'Unknown fund';

/** The same rule the pending-assets endpoint applies, so the two reads cannot disagree. */
const NON_PENDING_STATUSES: readonly SubscriptionStatusType[] = [
  SubscriptionStatus.Cancelled,
  SubscriptionStatus.Invested,
];

type SubscribedBoardEntry = MyInvestmentBoardEntryDto & { subscriptionId: string };

const isSubscribed = (entry: MyInvestmentBoardEntryDto): entry is SubscribedBoardEntry =>
  entry.subscriptionId !== null;

/**
 * The investor's own in-flight subscriptions across every fund: every entry
 * outside a final-stage column, minus the pre-subscription lead cards (Link
 * Clicked / Due Diligence Viewed), which have no subscription to show here.
 * `column.final` is the board's own terminal-stage marker, reused rather than
 * a second, hand-maintained list of terminal stages on the frontend.
 */
const collectPendingEntries = (
  columns: MyInvestmentBoardColumnDto[] = [],
): SubscribedBoardEntry[] =>
  columns
    .filter((column) => !column.final)
    .flatMap((column) => column.entries)
    .filter(isSubscribed);

export const PendingAssetsSection = () => {
  const isMobile = useIsMobile();
  const { data: board, isLoading: isBoardLoading } = useQuery(_getMyInvestmentBoard());

  const pendingEntries = useMemo(() => collectPendingEntries(board?.columns), [board]);

  const subscriptionQueries = useQueries({
    queries: pendingEntries.map((entry) =>
      _getWizardResumeQuery({ fundId: entry.fund.id, subscriptionId: entry.subscriptionId }),
    ),
  });

  const rows: PendingAssetRow[] = pendingEntries.flatMap((entry, index) => {
    const subscription = subscriptionQueries[index]?.data?.subscription;
    if (!subscription) return [];
    // The board cannot answer this on its own: Invested is not a `final` stage, so a settled card
    // still passes the column filter above. Mirrors the pending-assets endpoint's own
    // `status NOT IN ('Cancelled', 'Invested')` so both reads agree until this page moves over to it.
    if (NON_PENDING_STATUSES.includes(subscription.status)) return [];

    const investment: Investment = projectSubscriptionToInvestment(subscription, entry.fund.id);

    return [
      {
        key: entry.id,
        href: entry.fund.slug ? `${PagePaths.allFunds}/${entry.fund.slug}` : undefined,
        fundName: entry.fund.name ?? UNKNOWN_FUND_NAME,
        amountLabel: `${formatDefaultFractionTwo(investment.amount)} ${investment.currency}`,
        status: deriveInvestorStatus(investment),
        lastActivityAt: investment.lastActivityAt,
        investment,
        fund: entry.fund,
      },
    ];
  });

  const isLoading = isBoardLoading || subscriptionQueries.some((query) => query.isLoading);

  const columns: TableColumn<PendingAssetRow>[] = [
    {
      key: 'fundName',
      header: 'Fund',
      minWidth: 256,
      cell: (row) => <TextCell title={row.fundName} />,
    },
    {
      key: 'amountLabel',
      header: 'Amount',
      width: '160px',
      cell: (row) => <TextCell title={row.amountLabel} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '192px',
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'lastActivityAt',
      header: 'Last Activity',
      width: TIME_CELL_MIN_WIDTH,
      align: 'right',
      cell: (row) => <TimeCell date={row.lastActivityAt} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      // Fits the longest primary label, "Payment details", beside the menu button.
      width: '224px',
      align: 'right',
      pinned: true,
      cell: (row) => <PendingAssetActions investment={row.investment} fund={row.fund} />,
    },
  ];

  return (
    <section>
      <SectionHeader>
        <Typography as="h3" variant="h5" color={theme.colors.text.primary}>
          Pending Assets
        </Typography>
      </SectionHeader>
      <SectionBody>
        {isLoading ? (
          <LoadingState>
            <Spinner />
          </LoadingState>
        ) : rows.length === 0 ? (
          <EmptyState
            variant="tertiary"
            title="No investments in progress."
            action={{ label: 'Browse funds', to: PagePaths.allFunds }}
          />
        ) : isMobile ? (
          <PendingAssetsMobile rows={rows} />
        ) : (
          <Table
            columns={columns}
            rows={rows}
            size="lg"
            minTableWidth={getTableMinWidth(columns)}
          />
        )}
      </SectionBody>
    </section>
  );
};

export default PendingAssetsSection;
